// Finishing a Google (PKCE) sign-in when the browser comes back with ?code=.
//
// WHY THIS EXISTS
// This is the ONE consumer of a PKCE callback in this app. detectSessionInUrl is
// off (see lib/supabaseClient for the reason): a code and its verifier are
// single-use, and two consumers means the second one always fails.
//
// It also has to exist at all. auth-js 2.110 only recognizes a callback when the
// verifier is already in this origin's storage:
//
//   async _isPKCECallback(params) {
//     const currentStorageContent = await getItemAsync(this.storage, `${this.storageKey}-code-verifier`)
//     return !!(params.code && currentStorageContent)
//   }
//
// and when that is false the whole URL is skipped — no /auth/v1/token request,
// no error, ?code= left in the address bar on a page that never signs in.
// Handling it here means a failure is always reported rather than swallowed.
//
// Every PKCE return this app receives comes through here: Google sign-in, email
// confirmation, and password recovery — which the exchange reports separately,
// because a recovery link must open the "set a new password" screen and not a
// signed-in account.
//
// Exactly one exchange happens per page load: an auth code is single-use, and a
// second attempt would spend a code that is already gone. React StrictMode
// mounts effects twice in development, so this cannot rely on a component.

// The parameters an OAuth return adds. Only these are ever removed from the URL.
const CALLBACK_PARAMS = ["code", "state", "error", "error_code", "error_description"];

// What came back on the URL. Pure — the caller supplies the query string.
function readAuthCallback(search) {
  let params;
  try { params = new URLSearchParams(String(search || "")); } catch (e) { return { present: false }; }
  const code = params.get("code");
  const error = params.get("error");
  const errorCode = params.get("error_code");
  const errorDescription = params.get("error_description");
  return {
    present: Boolean(code || error || errorCode),
    code: code || null,
    error: error || null,
    errorCode: errorCode || null,
    errorDescription: errorDescription || null,
  };
}

// The same URL with the OAuth parameters taken out. Origin, path, any other
// query parameter and the hash are all preserved — a preview build must stay on
// the preview, and a prefilled order must keep its prefill.
function strippedUrl(href) {
  try {
    const url = new URL(String(href));
    for (const p of CALLBACK_PARAMS) url.searchParams.delete(p);
    return url.toString();
  } catch (e) {
    return String(href);
  }
}

// Take them out of the address bar without navigating anywhere.
function cleanCallbackUrl(win) {
  try {
    const w = win || (typeof window !== "undefined" ? window : null);
    if (!w || !w.history || typeof w.history.replaceState !== "function") return false;
    w.history.replaceState({}, "", strippedUrl(w.location.href));
    return true;
  } catch (e) { return false; }
}

// Provider errors come back as machine strings. The customer gets a sentence.
function friendlyCallbackError(cb) {
  const code = String((cb && cb.errorCode) || (cb && cb.error) || "");
  if (/access_denied/i.test(code)) return "Sign-in was cancelled. You can try again whenever you're ready.";
  if (/expired/i.test(code)) return "That sign-in link has expired. Please try signing in again.";
  return "We couldn't complete that sign-in. Please try again.";
}

// One exchange per page load, however many components ask for it.
let pending = null;

// Finish the sign-in if this page load is an OAuth return.
//
//   { handled: false }                       an ordinary page load
//   { handled: true, ok: true }              signed in; the session is live
//   { handled: true, ok: false, message }    it could not be completed
//
// Never throws: a failed sign-in must leave a usable page.
function completeAuthCallback(opts) {
  if (pending) return pending;                       // StrictMode, two components, a re-render
  const o = opts || {};
  const win = o.win || (typeof window !== "undefined" ? window : null);
  const client = o.client === undefined ? require("./supabaseClient").supabase : o.client;

  const cb = readAuthCallback(win && win.location ? win.location.search : "");
  if (!cb.present) return Promise.resolve({ handled: false, ok: false });

  pending = (async () => {
    // The provider refused, or the user cancelled. Nothing to exchange.
    if (!cb.code) {
      cleanCallbackUrl(win);
      return { handled: true, ok: false, message: friendlyCallbackError(cb) };
    }
    if (!client || !client.auth || typeof client.auth.exchangeCodeForSession !== "function") {
      return { handled: true, ok: false, message: "Sign-in is temporarily unavailable. Please try again later." };
    }
    try {
      // The verifier is read out of storage by supabase-js itself. It is never
      // touched, moved or logged here — and nothing is cleared before this runs.
      // This call also SAVES the session and notifies subscribers, so there is
      // nothing left to do afterwards but read it.
      const { data, error } = await client.auth.exchangeCodeForSession(cb.code);
      // ONLY NOW. The code stays in the address bar for the whole exchange, so
      // nothing can strip it out from under the call, and a reload before it
      // finishes still carries a code rather than landing on a blank page.
      cleanCallbackUrl(win);
      if (error || !data || !data.session) {
        return {
          handled: true, ok: false,
          message: exchangeFailureMessage(error),
          // Which Supabase error this actually was. A code, never a token or a
          // verifier — this is what turns "it didn't work" into a diagnosis.
          errorCode: failureCode(error),
        };
      }
      // A password-recovery link is also a ?code= return. The exchange reports
      // which kind it was — the redirect type travels with the verifier — so the
      // reset screen opens instead of an ordinary signed-in account. Reported
      // here rather than relying on catching the PASSWORD_RECOVERY event, which
      // fires during the exchange and could land before anyone is listening.
      return { handled: true, ok: true, recovery: data.redirectType === "recovery" };
    } catch (e) {
      cleanCallbackUrl(win);
      return { handled: true, ok: false, message: exchangeFailureMessage(e), errorCode: failureCode(e) };
    }
  })();
  return pending;
}

// The machine-readable reason an exchange failed, safe to show and to log.
// Only ever an identifier the library or the auth server produced — never the
// code, the verifier, a token, or a raw server message.
function failureCode(error) {
  if (!error) return "no_session_returned";
  const code = error.code || error.name;
  return code ? String(code) : "unknown_error";
}

// ── STARTING a sign-in ────────────────────────────────────────────────────────
//
// signInWithOAuth mints a NEW code verifier and overwrites the stored one, then
// navigates. So a second call before the browser has left the page throws away
// the verifier belonging to the flow that is actually in flight: the provider
// comes back with a code from flow A while storage holds flow B's verifier, the
// auth server refuses the pair, and the customer is told the link has already
// been used. Nothing about that is recoverable at the callback — it has to be
// prevented at the start.
//
// The button's `disabled={busy}` cannot prevent it. Setting state is
// asynchronous: two clicks landing in the same React batch both run before the
// re-render that disables anything. This latch lives outside React, so the
// second click cannot start a second flow whatever the UI is doing.
let oauthInFlight = false;

// Begin a provider sign-in. Returns:
//   { started: true }                  the browser is on its way to the provider
//   { started: false, reason }         nothing was done, and nothing was broken
async function startOAuthSignIn(client, provider, redirectTo) {
  if (oauthInFlight) return { started: false, reason: "already_in_progress" };
  if (!client || !client.auth || typeof client.auth.signInWithOAuth !== "function") {
    return { started: false, reason: "unavailable" };
  }
  oauthInFlight = true;
  try {
    const { error } = await client.auth.signInWithOAuth({ provider, options: { redirectTo } });
    if (error) {
      // Nothing is in flight after a refusal, so a retry must be allowed.
      oauthInFlight = false;
      return { started: false, reason: "provider_error", error };
    }
    // Deliberately left latched: this page is navigating away.
    return { started: true };
  } catch (e) {
    oauthInFlight = false;
    return { started: false, reason: "provider_error", error: e };
  }
}

// Tests only.
function resetOAuthInFlight() { oauthInFlight = false; }

// Why an exchange failed, in the customer's terms. A missing verifier means the
// sign-in was started somewhere else — a different site, or a browser that
// discarded the storage — which they can fix by starting again here.
function exchangeFailureMessage(error) {
  // Real failures arrive in several shapes: an AuthError with a `code`, a class
  // whose `name` carries the meaning, or a plain Error with only a message. All
  // three are inspected so the useful case is not missed by looking in one place.
  const text = error
    ? [error.code, error.name, error.message].filter(Boolean).map(String).join(" ")
    : "";
  if (/verifier/i.test(text)) {
    return "We couldn't finish signing you in, because this sign-in was started somewhere else. Please click Continue with Google again on this page.";
  }
  // The auth server could not match the returned code to a live sign-in. It had
  // already been completed, it timed out, or the pair did not match.
  if (/flow_state|flow state/i.test(text)) {
    return "That sign-in couldn't be completed — it may have taken too long or already been used. Please click Continue with Google again.";
  }
  if (/expired|invalid/i.test(text)) {
    return "That sign-in link has already been used or has expired. Please sign in again.";
  }
  return "We couldn't complete that sign-in. Please try again.";
}

// Where a provider should send the browser back to: THIS site, whichever it is.
// A preview returns to the preview and production returns to production, with no
// deployment URL written into the code.
function siteRedirectUrl(win) {
  const w = win || (typeof window !== "undefined" ? window : null);
  return w && w.location ? w.location.origin : "";
}

// Tests only: forget that an exchange already happened on this page load.
function resetAuthCallbackState() { pending = null; }

module.exports = {
  CALLBACK_PARAMS,
  readAuthCallback,
  strippedUrl,
  cleanCallbackUrl,
  friendlyCallbackError,
  exchangeFailureMessage,
  completeAuthCallback,
  failureCode,
  startOAuthSignIn,
  resetOAuthInFlight,
  siteRedirectUrl,
  resetAuthCallbackState,
};
